import { Injectable, computed, inject } from '@angular/core';
import { PaginatedResponse, PaginationParams } from '@shared/interfaces/pagination.interface';
import { ContactCreateRequest, ContactUpdateRequest } from '../../domain/contact.domain';
import { ContactRepository } from '../../domain/contact.repository';
import { ContactMapper } from '../../infrastructure/mappers/contact.mapper';
import { Contact, ContactDetail } from '../models/contact.model';
import { ContactStoreService } from './contact-store.service';
import { Router } from '@angular/router';
import { ROUTES } from '@app/shared/constants/routes.constant';

@Injectable({
  providedIn: 'root',
})
export class ContactService {
  private readonly _repository = inject(ContactRepository);
  private readonly _store = inject(ContactStoreService);
  private readonly _router = inject(Router);

  // Exponer señales del store para acceso directo desde componentes
  readonly selectedContact = this._store.selectedContact;
  readonly selectedContactDetail = this._store.selectedContactDetail;
  readonly isLoading = this._store.isLoading;
  readonly error = this._store.error;
  readonly hasMore = this._store.hasMore;
  readonly success = this._store.success;

  private readonly _redirectPath = computed(() =>
    this._store.totalContacts() > 1 ? ROUTES.ADMIN.CONTACTS.BASE : ROUTES.ADMIN.HOME.BASE
  );

  async getContacts(params: PaginationParams): Promise<PaginatedResponse<Contact>> {
    this._store.setLoading(true);
    this._store.clearError();

    try {
      const response = await this._repository.getContacts(params.page, params.pageSize);

      // Mapear DTOs de dominio a modelos de presentación
      const mappedItems = ContactMapper.toListItems(response.content);

      const totalPages = response.totalPages;

      // Actualizar estado en el store
      this._store.setHasMore(params.page < totalPages - 1);

      this._store.setTotalContacts(response.totalElements);

      return {
        items: mappedItems,
        totalItems: response.totalElements,
        currentPage: response.currentPage,
        totalPages,
        pageSize: response.pageSize,
        hasNextPage: params.page < totalPages - 1,
        hasPreviousPage: params.page > 0,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error loading contacts';
      this._store.setError(errorMessage);
      this._store.setHasMore(false);
      throw error;
    } finally {
      this._store.setLoading(false);
    }
  }

  async getContactDetail(id: string): Promise<ContactDetail> {
    this._store.setLoading(true);
    this._store.clearError();

    try {
      const response = await this._repository.getContactById(id);

      const contactDetail = ContactMapper.toDetail(response);
      this._store.setSelectedContactDetail(contactDetail);

      return contactDetail;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error loading contact details';
      this._store.setError(errorMessage);
      throw error;
    } finally {
      this._store.setLoading(false);
    }
  }

  async createContact(data: ContactCreateRequest): Promise<void> {
    this._store.setLoading(true);
    this._store.clearError();

    try {
      await this._repository.createContact(data);

      this._store.setSucces('¡Tu contacto ha sido guardado con éxito!');

      this._store.reset();

      this._router.navigate([this._redirectPath()]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error creating contact';
      this._store.setError(errorMessage);
      throw error;
    } finally {
      this._store.setLoading(false);
    }
  }

  async updateContact(id: string, data: ContactUpdateRequest): Promise<string> {
    this._store.setLoading(true);
    this._store.clearError();

    try {
      const response = await this._repository.updateContact(id, data);

      this._store.setSucces('¡Tu contacto ha sido actualizado con éxito!');

      this._store.reset();

      return response.contactUpdateMessage;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error updating contact';
      this._store.setError(errorMessage);
      throw error;
    } finally {
      this._store.setLoading(false);
    }
  }

  async deleteContact(id: string): Promise<void> {
    this._store.setLoading(true);
    this._store.clearError();

    try {
      await this._repository.deleteContact(id);

      this._store.reset();

      this._store.setSucces("Contacto eliminado con éxito.");

      this._router.navigate([this._redirectPath()]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error deleting contact';
      this._store.setError(errorMessage);
      throw error;
    } finally {
      this._store.setLoading(false);
    }
  }

}
